'use client';
import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';

import { StockCount } from './StockCountContent';

interface Props {
  open: boolean;
  count: StockCount;
  onClose: () => void;
  onApprove: (countId: string, notes: string) => void;
  onReject: (countId: string, reason: string) => void;
}

export default function StockCountApprovalModal({ open, count, onClose, onApprove, onReject }: Props) {
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const varianceItems = count.lineItems.filter((li) => (li.variance ?? 0) !== 0);
  const matchedItems = count.lineItems.filter((li) => (li.variance ?? 0) === 0);
  const totalVarianceValue = count.lineItems.reduce((acc, li) => acc + (li.varianceValue ?? 0), 0);

  const handleConfirm = async () => {
    if (action === 'reject' && !rejectionReason.trim()) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setIsSubmitting(false);
    if (action === 'approve') {
      onApprove(count.id, approvalNotes);
    } else if (action === 'reject') {
      onReject(count.id, rejectionReason);
    }
  };

  const fieldClass = 'w-full px-3 py-2 border border-border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Review Count — ${count.countNumber}`}
      subtitle={`${count.site} · ${count.countDate} · Submitted by ${count.createdBy}`}
      size="2xl"
      footer={
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="InformationCircleIcon" size={14} />
            Approving will post stock adjustments immediately and lock this count permanently.
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors">
              Cancel
            </button>
            {action === null && (
              <>
                <button
                  onClick={() => setAction('reject')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-600 hover:bg-red-100 transition-colors"
                >
                  <Icon name="XCircleIcon" size={15} />
                  Reject Count
                </button>
                <button
                  onClick={() => setAction('approve')}
                  className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-600 hover:bg-teal-700 transition-all active:scale-95"
                >
                  <Icon name="CheckCircleIcon" size={15} />
                  Approve & Post
                </button>
              </>
            )}
            {action !== null && (
              <button
                onClick={handleConfirm}
                disabled={isSubmitting || (action === 'reject' && !rejectionReason.trim())}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-600 transition-all active:scale-95 disabled:opacity-60 ${
                  action === 'approve' ?'bg-teal-600 text-white hover:bg-teal-700' :'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <><Icon name="ArrowPathIcon" size={15} className="animate-spin" /> Processing…</>
                ) : action === 'approve' ? (
                  <><Icon name="CheckCircleIcon" size={15} /> Confirm Approval</>
                ) : (
                  <><Icon name="XCircleIcon" size={15} /> Confirm Rejection</>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Items', value: count.totalItems, color: 'text-foreground' },
            { label: 'Matched (No Variance)', value: matchedItems.length, color: 'text-teal-600' },
            { label: 'With Variance', value: varianceItems.length, color: varianceItems.length > 0 ? 'text-red-600' : 'text-teal-600' },
            {
              label: 'Net Variance Value',
              value: `${totalVarianceValue >= 0 ? '+' : ''}€${totalVarianceValue.toFixed(2)}`,
              color: totalVarianceValue < 0 ? 'text-red-600' : totalVarianceValue > 0 ? 'text-amber-600' : 'text-teal-600',
            },
          ].map((s) => (
            <div key={`sum-${s.label}`} className="bg-muted/50 border border-border rounded-xl p-3 text-center">
              <p className={`text-xl font-700 ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Variance items highlighted */}
        {varianceItems.length > 0 && (
          <div>
            <h3 className="text-sm font-600 text-foreground mb-2 flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={15} className="text-amber-500" />
              Items with Variance ({varianceItems.length})
            </h3>
            <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-amber-100 text-amber-800">
                    <th className="px-3 py-2 text-left font-600">Item</th>
                    <th className="px-3 py-2 text-center font-600">System Qty</th>
                    <th className="px-3 py-2 text-center font-600">Physical Qty</th>
                    <th className="px-3 py-2 text-center font-600">Variance</th>
                    <th className="px-3 py-2 text-center font-600">Variance %</th>
                    <th className="px-3 py-2 text-right font-600">Value Impact</th>
                    <th className="px-3 py-2 text-left font-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {varianceItems.map((li) => (
                    <tr key={`var-${li.id}`} className="border-t border-amber-200">
                      <td className="px-3 py-2">
                        <p className="font-600 text-foreground">{li.itemCode}</p>
                        <p className="text-muted-foreground text-[10px]">{li.itemName}</p>
                      </td>
                      <td className="px-3 py-2 text-center font-500">{li.systemQty} {li.uom}</td>
                      <td className="px-3 py-2 text-center font-600">{li.physicalQty} {li.uom}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`font-700 ${(li.variance ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.variance ?? 0) > 0 ? '+' : ''}{li.variance} {li.uom}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`font-600 ${(li.variancePct ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.variancePct ?? 0) > 0 ? '+' : ''}{li.variancePct?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-700 ${(li.varianceValue ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.varianceValue ?? 0) >= 0 ? '+' : ''}€{li.varianceValue?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground italic">{li.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Matched items */}
        <div>
          <h3 className="text-sm font-600 text-foreground mb-2 flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={15} className="text-teal-600" />
            Matched Items — No Variance ({matchedItems.length})
          </h3>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="px-3 py-2 text-left font-600">Item Code</th>
                  <th className="px-3 py-2 text-left font-600">Item Name</th>
                  <th className="px-3 py-2 text-center font-600">System Qty</th>
                  <th className="px-3 py-2 text-center font-600">Physical Qty</th>
                  <th className="px-3 py-2 text-center font-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((li, i) => (
                  <tr key={`match-${li.id}`} className={`border-t border-border ${i % 2 === 0 ? 'bg-card' : 'bg-muted/20'}`}>
                    <td className="px-3 py-2 font-600 text-secondary">{li.itemCode}</td>
                    <td className="px-3 py-2">{li.itemName}</td>
                    <td className="px-3 py-2 text-center">{li.systemQty} {li.uom}</td>
                    <td className="px-3 py-2 text-center font-600">{li.physicalQty} {li.uom}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] font-600 text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                        <Icon name="CheckIcon" size={10} /> Matched
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action confirmation area */}
        {action === 'approve' && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <h4 className="text-sm font-600 text-teal-800 mb-2 flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={15} />
              Approval Notes (optional)
            </h4>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Add any notes for the approval record…"
              rows={2}
              className="w-full px-3 py-2 border border-teal-300 rounded-lg text-sm bg-white text-foreground outline-none focus:ring-2 focus:ring-teal-300 transition-all resize-none"
            />
            <p className="text-xs text-teal-700 mt-2">
              ⚠ Approving will post ADJ-IN/OUT transactions for all {varianceItems.length} variance item(s) and lock this count permanently.
            </p>
          </div>
        )}

        {action === 'reject' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <h4 className="text-sm font-600 text-red-800 mb-2 flex items-center gap-2">
              <Icon name="XCircleIcon" size={15} />
              Rejection Reason <span className="text-red-500">*</span>
            </h4>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide a mandatory reason for rejection…"
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white text-foreground outline-none focus:ring-2 focus:ring-red-300 transition-all resize-none ${!rejectionReason.trim() ? 'border-red-400' : 'border-red-300'}`}
            />
            {!rejectionReason.trim() && (
              <p className="text-red-600 text-[11px] mt-1">Rejection reason is required.</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
